import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Divider, Chip, Stack, IconButton
} from '@mui/material';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../../App.js';
import ConfirmModal from '../../../components/ConfirmModal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Berlin");

const formatTime = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).tz().format('HH:mm');
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = dayjs(dateString).tz();
    const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const weekday = weekdays[date.day()];
    return `${weekday}, ${date.format('DD.MM.YYYY')}`;
};

const EventDetailsModal = ({ event, isOpen, onClose, onEdit, onRefresh }) => {
    const [confirmConfig, setConfirmConfig] = React.useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
        type: 'question'
    });

    if (!event) return null;

    const handleDelete = () => {
        setConfirmConfig({
            open: true,
            title: 'Termin löschen',
            message: 'Bist du sicher, dass du diesen Termin und alle zugehörigen Buchungen löschen möchtest?',
            onConfirm: executeDelete,
            type: 'delete'
        });
    };

    const executeDelete = async () => {
        try {
            await axios.delete(`${API_BASE_URL}/api/events/${event._id}`);
            toast.success("Event erfolgreich gelöscht");
            onClose();
            onRefresh();
        } catch (err) {
            toast.error("Fehler beim Löschen des Events");
        }
    };

    const handleToggleStatus = () => {
        const action = event.status === 0 ? 'deaktivieren' : 'reaktivieren';
        setConfirmConfig({
            open: true,
            title: `Termin ${action}`,
            message: `Möchtest du diesen Termin wirklich ${action}? Alle zukünftigen Daten werden entsprechend geändert.`,
            onConfirm: executeToggleStatus,
            type: 'warning'
        });
    };

    const executeToggleStatus = async () => {
        try {
            const response = await axios.patch(`${API_BASE_URL}/api/events/${event._id}/toggle-status`);
            toast.success(response.data.message);
            onClose();
            onRefresh();
        } catch (err) {
            toast.error("Fehler beim Ändern des Event-Status");
        }
    };

    const handleEdit = () => {
        onEdit(event);
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 2, bgcolor: 'primary.main', color: 'white', position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mr: 4 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        {event.name}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'white',
                    }}
                >
                    <MdClose />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ pt: 2.5, mt: 1 }}>
                <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 85, fontWeight: 500 }}>
                            Kategorie:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {event.type || 'N/A'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 85, fontWeight: 500 }}>
                            Zeitraum:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDate(event.startDate) === formatDate(event.endDate) 
                                ? formatDate(event.startDate) 
                                : `${formatDate(event.startDate)} - ${formatDate(event.endDate)}`}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 85, fontWeight: 500 }}>
                            Uhrzeit:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatTime(event.startTime)} - {formatTime(event.endTime)}
                        </Typography>
                    </Box>

                    {event.coach && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 85, fontWeight: 500 }}>
                                Trainer:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {event.coach}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 85, fontWeight: 500 }}>
                            {event.courts?.length === 1 ? 'Platz:' : 'Plätze:'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {event.courts?.map((court, idx) => (
                                <Chip 
                                    key={idx} 
                                    label={court} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.75rem', fontWeight: 500 }}
                                />
                            )) || <Typography variant="body2" color="text.secondary">Keine Plätze</Typography>}
                        </Box>
                    </Box>

                    {event.bookingCount > 0 && (
                        <>
                            <Divider />
                            <Box 
                                sx={{ 
                                    bgcolor: 'primary.50', 
                                    p: 2, 
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'primary.light'
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                        Termine ({event.bookingCount})
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    Klicke auf das Kalender-Icon in der Termin-Liste, um alle Daten anzuzeigen
                                </Typography>
                            </Box>
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, pt: 2, display: 'block' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                        onClick={handleEdit}
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{ py: 1.5, fontWeight: 'bold' }}
                    >
                        Bearbeiten
                    </Button>

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            onClick={handleToggleStatus}
                            variant="outlined"
                            color={event.status === 0 ? "warning" : "success"}
                            size="medium"
                            fullWidth
                            disabled={dayjs(event.endDate).tz().isBefore(dayjs.tz().startOf('day'))}
                        >
                            {event.status === 0 ? 'Deaktivieren' : 'Reaktivieren'}
                        </Button>

                        <Button
                            onClick={handleDelete}
                            variant="outlined"
                            color="error"
                            size="medium"
                            fullWidth
                        >
                            Löschen
                        </Button>
                    </Box>
                </Box>
            </DialogActions>
            <ConfirmModal 
                open={confirmConfig.open}
                onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
            />
        </Dialog>
    );
};

export default EventDetailsModal;
